﻿/** The list of all supported locales */
export let LocaleInfo: CultureMap = require("./LocaleInfo.json");

/** Defines the default locale for the application. */
export const DefaultLocale: string = "en";

/** Represents a single locale's information */
export interface CultureInfo {
    globalizeName: string;
    labelFile: string;
    cultureFileExists: boolean;
    nativeName: string;
    isDefault?: boolean | undefined;
    isRTL?: boolean | undefined;
    englishName?: string;
}

export interface CultureInfoIndirect {
    globalizeName: string;
    parent: string;
}

export interface CultureMap {
    [locale: string]: CultureInfo | CultureInfoIndirect;
}

export function isIndirectCulture(info: CultureInfo | CultureInfoIndirect): info is CultureInfoIndirect {
    return (<CultureInfoIndirect>info).parent !== undefined;
}

export function languageIsRtl(locale: string): boolean {
    const language = getSupportedLocale(locale).toUpperCase();
    const currentLocale = LocaleInfo[language];

    return (currentLocale && !isIndirectCulture(currentLocale) && currentLocale.isRTL === true);
}

/**
 * Sets the 'dir' attribute on the root HTML element to indicate the text direction (LTR/RTL)
 *
 * @param languageName The name of the language whose culture contains the text direction.
 */
export function setTextDirection(languageName: string): void {

    const rtlAttribute = languageIsRtl(languageName) ? "rtl" : undefined;
    const htmlElements = document.getElementsByTagName("html");

    if (htmlElements.length > 0) {
        if (rtlAttribute === undefined) {
            htmlElements[0].removeAttribute("dir");
        } else {
            htmlElements[0].dir = rtlAttribute;
        }
    }
}

/**
 * Gets the locale that's supported based on the provided locale.
 * @param locale The locale for which to find the supported locale.
 * @returns The supported locale.
 */
export function getSupportedLocale(locale: string): string {
    const upperCaseLocale = locale.toUpperCase();
    let supportedLanguage: string;
    if (upperCaseLocale in LocaleInfo) {
        let locale = LocaleInfo[upperCaseLocale];
        if (isIndirectCulture(locale)) {
            supportedLanguage = getSupportedLocale(locale.parent);
        } else {
            supportedLanguage = locale.globalizeName;
        }
    } else {
        supportedLanguage = DefaultLocale;
    }
    return supportedLanguage;
}

/**
 * Loads a single globalize culture file.
 * @param locale The locale to load the globalize culture for
 */
export function loadGlobalizeCulture(locale: string): Promise<void> {
    const currentLocale = LocaleInfo[locale.toUpperCase()];

    if (!currentLocale || isIndirectCulture(currentLocale)) {
        return Promise.reject(`'${locale}' is not a supported culture`);
    }

    if (!currentLocale.cultureFileExists) {
        return Promise.resolve();
    }

    return import(
        /* webpackChunkName: "[request]" */
        `globalize/lib/cultures/globalize.culture.${currentLocale.globalizeName}`
    );
}
